/* TIPS AND TRICKS
* If you plan on using the columnOrder feature, do this -
* install the following packages
* npm i -S react-dnd react-dnd-html5-backend
* then you need to wrap the root component of your app with the HOC provided
* Example -
* export default DragDropContext(HTML5Backend)(App)
==================================================================
* When reordering columns, make sure to notice that fixed columns are
* not eligible for rearrangement. This also means that DataTable's parent
* component will have to handle merging this column back into the columns returned
* from the ColumnConfig component.
*/

import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import cx from 'classnames'
import ColumnHeader from './ColumnHeader'
import InfiniteScroll from '../scrollbars/InfiniteScroll'
import CircleLoader from '../CircleLoader'
import ErrorState from '../ErrorState'
import TextOverflow from '../TextOverflow'
import DialogContainer from '../DialogContainer'
import ColumnOrderConfig from './ColumnOrderConfig'
import DragSelectionContainer from '../DragSelection'
import DragSelectable from '../../hoc/DragSelectable'
import Timing from '../../hoc/Timing'
import DataTableRow from './DataTableRow'
import { removeDragImage, getCurrentDragClientXY } from '../../utils'
import TextOverflowCenter from '../TextOverflowCenter'

function setElTransform(el, transform) {
  if (el) {
    el.style.transform = el.style.WebkitTransform = transform
  }
}

class DataTable extends React.Component {
  static displayName = 'DataTable'

  static propTypes = {
    columns: T.arrayOf(T.shape()).isRequired,

    columnOrder: T.arrayOf(T.string), //optional list of keys, will use the order of `columns` by default
    onColumnOrderChange: T.func, //(newColumnIds) - if not specified the config button will not be shown
    onColumnOrderReset: T.func,

    sort: T.any,
    sortedColumn: T.shape({
      key: T.string,
      direction: T.oneOf(['asc', 'desc'])
    }),
    onColumnSort: T.func, //(columnDef, asc_or_desc)
    onColumnResize: T.func, //(columnDef, newWidth)
    onDragSelection: T.func, // (selectedtKeys, bool shiftKeyHeld)

    data: T.arrayOf(T.object),
    getRowClass: T.func, //(rowData, rowIndex)

    isLoading: T.bool,
    error: T.any,

    isLoadingNextPage: T.bool,
    hasNextPage: T.bool,
    onRequestNextPage: T.func,

    className: T.string,
    style: T.object,
    noResultsText: T.node,
    rowHeight: T.number,
    scrollBufferSize: T.number,
    rowClick: T.func,
    rowDblClick: T.func,
    cellProps: T.object, // Additional props to pass to renderValue calls

    // For ScrollBars/InfiniteScroll
    slimShady: T.bool,
    slimShadyDarker: T.bool,

    // From Timing
    setTimer: T.func.isRequired,
    clearTimer: T.func.isRequired
  }

  static defaultProps = {
    rowHeight: 30,
    scrollBufferSize: 10,
    noResultsText: 'No Data'
  }

  constructor(props) {
    super(props)
    this.state = {
      firstRenderedItem: 0,
      lastRenderedItem: 0,
      isScrolledHorizontally: false,
      configuringColumns: false,
      draggingColumn: null,
      resizingColumn: false
    }
  }

  UNSAFE_componentWillMount() {
    this._tableId = `data_table_${Math.round(Math.random() * 10e12)}`
    this._latestSelectedIdx = 0 //Used for `shift+click` range selection
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isLoading || nextProps.sort !== this.props.sort) {
      if (this._scrollerRef) this._scrollerRef.resetScrollPosition() //Reset scroll position on loading to prevent immediate nextPage request
      this.setState({
        firstRenderedItem: 0,
        lastRenderedItem: 0
      })
    }
    // when getting updated column order, clear the temporary stored order
    if (!_.isEqual(nextProps.columnOrder, this.props.columnOrder)) {
      delete this._tempColumnOrder
    }
  }

  componentDidUpdate() {
    if (this._scrollerRef) {
      this._updateScrollMetrics(this._scrollerRef.getScrolledElement())
    }
  }

  _getOrderedColumns = () => {
    const columns = this.props.columns
    const columnsByKey = _.indexBy(columns, 'key')
    const fixedColumns = columns.filter(c => !!c.fixed)
    let normalColumns =
      this._tempColumnOrder || this.props.columnOrder || _.pluck(columns, 'key')
    normalColumns = _.uniq(
      normalColumns.filter(key => columnsByKey[key] && !columnsByKey[key].fixed)
    ) //remove bad/duped keys and fixed cols
    normalColumns = normalColumns.map(key => columnsByKey[key]) //map keys to col def objects
    return {
      fixedColumns,
      normalColumns,
      allColumns: fixedColumns.concat(normalColumns)
    }
  }

  _onColumnsConfigSave = newColumnOrder => {
    this.props.onColumnOrderChange(newColumnOrder)
    this._onColumnsConfigClose()
  }

  _onColumnsConfigReset = () => {
    this.props.onColumnOrderReset()
    this._onColumnsConfigClose()
  }

  _onColumnsConfigClose = () => {
    this.setState({ configuringColumns: false })
  }

  _onColumnsConfigClick = () => {
    this.setState({ configuringColumns: true })
  }

  _onColumnSort = (column, dir) => {
    const fn = this.props.onColumnSort
    if (fn) fn(column, dir)
  }

  _onColumnResizeStart = (/*column, startWidth*/) => {
    this.setState({ resizingColumn: true })
  }

  _onColumnResizeEnd = (column, newWidth) => {
    this.setState({ resizingColumn: false })
    const fn = this.props.onColumnResize
    if (fn) fn(column, newWidth)
  }

  _bindScrollerRef = scroller => {
    if (scroller) {
      this._updateScrollMetrics(scroller.getScrolledElement())
    }
    this._scrollerRef = scroller
  }

  _bindTableRef = el => {
    this._tableEl = el
  }

  _transformSelector = (selector, transform) => {
    _.each(this._tableEl.querySelectorAll(selector), el => {
      setElTransform(el, transform || '')
    })
  }

  _onScroll = node => {
    this._updateScrollMetrics(node)
    delete this._cachedTableX
  }

  _updateScrollMetrics = scrollNode => {
    const { props, state } = this
    const { data, rowHeight, scrollBufferSize } = props
    let firstVisibleItem = 0
    let lastVisibleItem = 0
    let { firstRenderedItem, lastRenderedItem } = state
    const { scrollTop, scrollLeft } = scrollNode
    const isScrolledHorizontally = scrollLeft > 0
    if (data && data.length) {
      const totalHeight = props.rowHeight * data.length
      const numVisibleRows = Math.ceil(scrollNode.clientHeight / rowHeight)
      firstVisibleItem = Math.floor(scrollTop / totalHeight * data.length)
      lastVisibleItem = Math.min(
        firstVisibleItem + numVisibleRows,
        data.length - 1
      )
      firstRenderedItem = Math.max(
        firstVisibleItem - firstVisibleItem % scrollBufferSize,
        0
      )
      lastRenderedItem = Math.min(
        firstRenderedItem + numVisibleRows + scrollBufferSize,
        data.length - 1
      )
    }
    if (
      firstRenderedItem !== state.firstRenderedItem ||
      lastRenderedItem !== state.lastRenderedItem ||
      isScrolledHorizontally !== state.isScrolledHorizontally
    ) {
      //console.log(`Rendering rows ${firstRenderedItem} - ${lastRenderedItem}`)
      this.setState({
        firstRenderedItem,
        lastRenderedItem,
        isScrolledHorizontally
      })
    }
    this._visibleRows = [firstVisibleItem, lastVisibleItem]

    const table = this._tableEl
    if (table) {
      // Adjust vertical position of header
      this._transformSelector(
        'thead',
        `translate3d(0, ${scrollTop - firstRenderedItem * rowHeight}px, 10px)`
      )

      // Adjust horizontal positions of fixed columns
      const fixedColCount = props.columns.filter(c => c.fixed).length
      if (fixedColCount && !state.draggingColumn) {
        this._transformSelector(
          `.fixed_col`,
          `translate3d(${scrollLeft}px, 0, 10px)`
        )
      }
    }
  }

  _onScrollThresholdMet = () => {
    const fn = this.props.onRequestNextPage
    if (fn) fn()
  }

  _getDragPosInfo = (/*e*/) => {
    const clientXY = getCurrentDragClientXY()
    const draggingColumn = this.state.draggingColumn

    // Find delta based on x offset from the table
    const currX =
      clientXY.x -
      (this._cachedTableX ||
        (this._cachedTableX = this._tableEl.getBoundingClientRect().left))
    const deltaX = currX - draggingColumn.startX

    // Find the target column index for the current drag delta
    const { fixedColumns, normalColumns } = this._getOrderedColumns()
    let targetColIndex = draggingColumn.origColIndex
    let totalColWidths = 0
    const step = deltaX > 0 ? 1 : -1
    while (normalColumns[targetColIndex + step]) {
      const nextColWidth =
        draggingColumn.allColWidths[targetColIndex + step + fixedColumns.length]
      if (Math.abs(deltaX) > totalColWidths + nextColWidth / 2) {
        targetColIndex += step
        totalColWidths += nextColWidth
      } else break
    }

    return {
      deltaX,
      targetColIndex
    }
  }

  _syncDraggedCol = () => {
    this._transformSelector(
      '.dragging_col',
      `translate3d(${this._dragPosInfo.deltaX}px, 0, 10px)`
    )
  }

  _syncShiftedCols = () => {
    this.props.setTimer(
      () => {
        const { deltaX, targetColIndex } = this._dragPosInfo
        const { fixedColumns } = this._getOrderedColumns()
        const draggingColumn = this.state.draggingColumn
        const shift = draggingColumn.draggedColWidth * (deltaX > 0 ? -1 : 1)
        const startIndex =
          deltaX > 0 ? draggingColumn.origColIndex : targetColIndex
        const endIndex = deltaX > 0 ? targetColIndex : draggingColumn.origColIndex
        _.each(
          this._tableEl.querySelectorAll(
            '.data_table_col:not(.dragging_col):not(.fixed_col)'
          ),
          cell => {
            const i = cell.cellIndex - fixedColumns.length
            const transform = `translate3d(${i >= startIndex && i <= endIndex
              ? shift
              : 0}px, 0, 1px)`
            if (transform !== cell._lastShiftTransform) {
              setElTransform(cell, transform)
              cell._lastShiftTransform = transform
            }
          }
        )
      },
      'frame',
      'colShiftFrame'
    )
    delete this._colShiftTimer
  }

  _onColumnDragStart = e => {
    const columnKey = e.target.dataset.columnkey
    const { normalColumns } = this._getOrderedColumns()
    const colIndex = _.findIndex(normalColumns, { key: columnKey })
    const colWidth = e.target.offsetWidth
    const tableX = (this._cachedTableX = this._tableEl.getBoundingClientRect().left)
    this.setState({
      draggingColumn: {
        columnKey,
        origColIndex: colIndex,
        targetColIndex: colIndex,
        startX: e.clientX - tableX, //pos relative to table in case the table scrolls during drag
        draggedColWidth: colWidth,
        allColWidths: _.map(
          this._tableEl.tHead.rows[0].cells,
          hdr => hdr.offsetWidth
        ) //measure col widths just once
      }
    })

    // Remove native floating drag image
    removeDragImage(e)

    // If react-dnd is active in the page, it will call preventDefault on all dragstart events that are not
    // handled by it and don't have a native data type, so go ahead and set plaintext data to work around it.
    e.dataTransfer.setData('text/plain', e.target.innerText)
  }

  _onColumnDrag = e => {
    const draggingColumn = this.state.draggingColumn
    if (draggingColumn) {
      this._dragPosInfo = this._getDragPosInfo(e)

      // Shift visual position of dragged column's cells to track mouse, on every frame:
      this.props.setTimer(this._syncDraggedCol, 'frame', 'colDrag')

      // Shifting of other columns doesn't need to be as immediate, so throttle it to gain FPS
      if (!this._colShiftTimer) {
        this._colShiftTimer = this.props.setTimer(this._syncShiftedCols, 100)
      }
    }
  }

  _onColumnDragEnd = e => {
    // TODO ignore drag cancellation by esc key etc
    const { normalColumns } = this._getOrderedColumns()
    const { columnKey, origColIndex } = this.state.draggingColumn
    const { targetColIndex } = this._getDragPosInfo(e)
    const newColOrder = _.pluck(normalColumns, 'key')
    newColOrder.splice(origColIndex, 1)
    newColOrder.splice(targetColIndex, 0, columnKey)

    // Persist if it has changed
    if (!_.isEqual(newColOrder, this.props.columnOrder)) {
      this._onColumnsConfigSave(newColOrder)
      this._tempColumnOrder = newColOrder
    }

    // Reset all dragging states
    this.setState({ draggingColumn: null })
    this._transformSelector(`.data_table_col:not(.fixed_col)`, null) //remove column shift transforms
    this.props.clearTimer('colDrag')
    this.props.clearTimer(this._colShiftTimer)
    this.props.clearTimer('colShiftFrame')
    delete this._colShiftTimer
    delete this._dragPosInfo
  }

  /**
   * Get the index range of the rows currently scrolled into view. Does not include buffer rows.
   * @return {Array<Number>}
   */
  getVisibleRows = () => {
    return this._visibleRows || [0, 0]
  }

  /**
   * Scrolls the given row into view
   * @param {Number} rowIndex
   */
  scrollToRow = (rowIndex, animationTime) => {
    const scroller = this._scrollerRef
    if (scroller) {
      const rowHeight = this.props.rowHeight
      const { scrollTop, scrollBottom } = scroller.getScrollPosition()
      const rowY = rowIndex * rowHeight + rowHeight //adjust for header
      let scrollTo = null
      if (rowY < scrollTop + rowHeight) {
        scrollTo = rowY - rowHeight
      } else if (rowY + rowHeight > scrollBottom) {
        scrollTo = rowY + rowHeight - (scrollBottom - scrollTop)
      }
      if (animationTime) {
        scroller.scrollToAnimate(animationTime, scrollTo, 0)
      } else {
        scroller.scrollTo(0, scrollTo)
      }
    }
  }

  onRowClick = e => {
    const ev = e
    const rowIndex = e.currentTarget.dataset.rowindex
    const dataItem = this.props.data[rowIndex]
    return this.props.rowClick(ev, dataItem)
  }

  onRowDblClick = e => {
    const ev = e
    const rowIndex = e.currentTarget.dataset.rowindex
    const dataItem = this.props.data[rowIndex]
    return this.props.rowDblClick(ev, dataItem)
  }

  render() {
    const { props, state } = this
    const DragSelectableTableRow = DragSelectable(DataTableRow)
    const { data, rowHeight, getRowClass, sortedColumn, cellProps } = props
    const {
      firstRenderedItem,
      lastRenderedItem,
      isScrolledHorizontally,
      configuringColumns,
      draggingColumn
    } = state
    const { fixedColumns, normalColumns, allColumns } = this._getOrderedColumns()
    const canReorder = !!props.onColumnOrderChange

    const getCommonCellClasses = (col, colIndex) => {
      const isFixed = col && col.fixed
      const normalColIndex = colIndex - fixedColumns.length
      const origColIndex = draggingColumn && draggingColumn.origColIndex
      const targetColIndex = draggingColumn && draggingColumn.targetColIndex
      return cx(
        'data_table_col',
        isFixed && 'fixed_col',
        isFixed && colIndex === fixedColumns.length - 1 && 'last_fixed_col',
        draggingColumn &&
          draggingColumn.columnKey === col.key &&
          'dragging_col',
        draggingColumn &&
          targetColIndex < origColIndex &&
          normalColIndex >= targetColIndex &&
          normalColIndex < origColIndex &&
          'col_shifted_right',
        draggingColumn &&
          targetColIndex > origColIndex &&
          normalColIndex > origColIndex &&
          normalColIndex <= targetColIndex &&
          'col_shifted_left'
      )
    }

    const rows = []
    if (data && data.length) {
      for (
        let rowIndex = firstRenderedItem;
        rowIndex <= Math.min(lastRenderedItem, data.length - 1);
        rowIndex++
      ) {
        const dataItem = data[rowIndex]
        const clickFunc = this.props.rowClick ? this.onRowClick : _.noop
        const dblClickFunc = this.props.rowDblClick ? this.onRowDblClick : _.noop

        rows.push(
          <DragSelectableTableRow
            className={getRowClass ? getRowClass(dataItem, rowIndex) : null}
            data-rowindex={rowIndex}
            key={dataItem.selectableKey || rows.length}
            onClick={clickFunc}
            onDoubleClick={dblClickFunc}
            selectableKey={dataItem.selectableKey}
          >
            {allColumns.map((col, colIndex) => {
              const colTextOverflowStrategy = col.getTextOverflowStrategy && col.getTextOverflowStrategy(dataItem, rowIndex, colIndex)
              const textOverflowStrategy = colTextOverflowStrategy || 'right'
              const content = col.renderValue
                ? col.renderValue(
                    dataItem,
                    rowIndex,
                    colIndex,
                    cellProps
                  )
                : dataItem[col.key]

              return col
                ? <td
                    className={cx(
                      getCommonCellClasses(col, colIndex),
                      col.getCellClass &&
                        col.getCellClass(dataItem, rowIndex, colIndex),
                        {
                          'prevent_dragselection': col.key !== 'checkbox'
                        }
                    )}
                    data-columnkey={col.key}
                    key={col.key}
                  >
                    {textOverflowStrategy === 'middle' ? (
                      <TextOverflowCenter
                        value={content}
                        className="data_table_val"
                      />
                    ) : textOverflowStrategy === 'right' ? (
                      <TextOverflow className="data_table_val">
                        {content}
                      </TextOverflow>
                    ) : (
                      <div className="data_table_val">{content}</div>
                    )}
                  </td>
                : null
            })}
            <td className="right_spacer" role="presentation" />
          </DragSelectableTableRow>
        )
      }
    }

    const component = (
      <InfiniteScroll
        className={cx(
          'data_table',
          'pw_scrollbars_light',
          props.className,
          props.isLoadingNextPage && !_.isEmpty(props.data) && 'loading more',
          isScrolledHorizontally && 'scrolled_horiz',
          draggingColumn && 'has_dragging_col',
          state.resizingColumn && 'has_resizing_col'
        )}
        endOfList={
          !props.hasNextPage &&
          !props.isLoading &&
          !props.isLoadingNextPage &&
          !_.isEmpty(data)
        }
        isReadyForNextEvent={props.hasNextPage && !props.isLoadingNextPage}
        onScroll={this._onScroll}
        onThresholdMet={props.onRequestNextPage || _.noop}
        ref={this._bindScrollerRef}
        slimShady={props.slimShady}
        slimShadyDarker
        style={props.style}
      >
        {_.isEmpty(allColumns)
          ? <span className="no_items">No Columns Selected</span>
          : <div
              style={{
                height: data ? data.length * rowHeight + rowHeight : 'auto',
                paddingTop: firstRenderedItem * rowHeight
              }}
            >
              <table id={this._tableId} ref={this._bindTableRef}>
                <thead>
                  <tr>
                    {allColumns.map((col, colIndex) => {
                      const isFixed = col && col.fixed
                      return col
                        ? <ColumnHeader
                            className={cx(getCommonCellClasses(col, colIndex))}
                            column={col}
                            cellProps={cellProps}
                            key={col.key}
                            onDrag={
                              canReorder && !isFixed ? this._onColumnDrag : null
                            }
                            onDragEnd={
                              canReorder && !isFixed
                                ? this._onColumnDragEnd
                                : null
                            }
                            onDragStart={
                              canReorder && !isFixed
                                ? this._onColumnDragStart
                                : null
                            }
                            onResizeStart={this._onColumnResizeStart}
                            onResizeEnd={this._onColumnResizeEnd}
                            onSort={this._onColumnSort}
                            sorted={
                              sortedColumn && sortedColumn.key === col.key
                                ? sortedColumn.direction
                                : null
                            }
                          />
                        : null
                    })}
                    <th className="right_spacer" role="presentation" />
                  </tr>
                </thead>
                <tbody>
                  {rows}
                </tbody>
              </table>
              {_.isEmpty(props.data) &&
              !props.isLoading &&
              !props.isLoadingNextPage
                ? <span className="no_items">
                    {props.noResultsText}
                  </span>
                : null}
            </div>}

        {canReorder
          ? <span
              className="btn btn-icon icon-cog cfg_cols_btn"
              data-tooltip="Configure columns"
              onClick={this._onColumnsConfigClick}
            />
          : null}

        <CircleLoader
          overlay
          loading={
            props.isLoading ||
            (props.isLoadingNextPage && _.isEmpty(props.data))
          }
        />
        <ErrorState error={props.error} />

        {configuringColumns &&
          <DialogContainer modal>
            <ColumnOrderConfig
              columnOrder={_.pluck(normalColumns, 'key')}
              columns={props.columns}
              onCancel={this._onColumnsConfigClose}
              onReset={this._onColumnsConfigReset}
              onSave={this._onColumnsConfigSave}
            />
          </DialogContainer>}
      </InfiniteScroll>
    )
    return props.onDragSelection
      ? <DragSelectionContainer
          className="data_table_drag_selectable_rows_wrap"
          onSelection={props.onDragSelection}
          selectOnMouseMove={false}
          tagName="div"
        >
          {component}
        </DragSelectionContainer>
      : component
  }
}

export default Timing(DataTable)
